import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';

const TEAL = C.teal || C.secondary;
const NAVY = C.navy || C.text;
const WARM_WHITE = C.warmWhite || C.bg;

const AGE_CONTENT = {
  '4-5': {
    heading: 'The Pre-Reading Stage: Ages 4-5',
    stageName: 'Reader',
    milestones: [
      { id: 'letters', label: 'Recognizes letters of the alphabet' },
      { id: 'sounds', label: 'Knows that letters make sounds' },
      { id: 'rhymes', label: 'Can rhyme simple words (cat/hat)' },
      { id: 'read_to', label: 'Enjoys being read to' },
    ],
    descriptions: {
      pre: 'Just getting started with reading basics — Finley builds confidence through playful early-literacy practice.',
      emerging: 'Beginning to connect letters, sounds, and word play in a steady routine.',
      developing: 'Building strong early reading habits and starting to connect print to meaning.',
      strong: 'Ready to move from pre-reading skills into confident early decoding.',
    },
  },
  '6-7': {
    heading: 'The Decoding Stage: Ages 6-7',
    stageName: 'Decoder',
    milestones: [
      { id: 'sound_out', label: 'Sounds out unfamiliar words' },
      { id: 'sentences', label: 'Reads simple sentences aloud' },
      { id: 'sight_20', label: 'Recognizes 20+ sight words' },
      { id: 'independent_5', label: 'Reads independently for 5+ minutes' },
    ],
    descriptions: {
      pre: 'Just getting started with phonics — Finley will build the foundation step by step.',
      emerging: 'Building phonics foundations with early sight word recognition.',
      developing: 'Making strong progress — connecting sounds to words and building fluency.',
      strong: 'Reading confidently — ready to level up to fluency and comprehension.',
    },
  },
  '8-9': {
    heading: 'The Fluency Transition: Ages 8-9',
    stageName: 'Fluency Builder',
    milestones: [
      { id: 'chapter_books', label: 'Reads chapter books independently' },
      { id: 'comprehends_once', label: 'Understands what they read without re-reading' },
      { id: 'expression', label: 'Reads aloud with expression' },
      { id: 'summarize', label: 'Can summarize a story in their own words' },
    ],
    descriptions: {
      pre: 'Still building reading momentum — Finley strengthens fluency in short, achievable steps.',
      emerging: 'Growing fluency and confidence with connected text and guided comprehension.',
      developing: 'Reading is becoming smoother, with stronger understanding of longer passages.',
      strong: 'Fluent and thoughtful reading — ready for deeper comprehension and vocabulary growth.',
    },
  },
  '10-12': {
    heading: 'The Comprehension Stage: Ages 10-12',
    stageName: 'Comprehender',
    milestones: [
      { id: 'independent_20', label: 'Reads for 20+ minutes independently' },
      { id: 'context_vocab', label: 'Understands vocabulary from context' },
      { id: 'compare_texts', label: 'Can compare and contrast different texts' },
      { id: 'genres', label: 'Reads across genres (fiction, nonfiction, etc.)' },
    ],
    descriptions: {
      pre: 'Comprehension skills are still forming — Finley helps build structure and confidence.',
      emerging: 'Developing stronger understanding of text meaning, structure, and vocabulary.',
      developing: 'Comprehension is advancing with better critical thinking across texts.',
      strong: 'Reading with depth and independence — ready for advanced multi-genre challenges.',
    },
  },
};

function computeLevel(checkedCount) {
  if (checkedCount === 0) return 'pre';
  if (checkedCount <= 2) return 'emerging';
  if (checkedCount === 3) return 'developing';
  return 'strong';
}

function stageLabel(level, stageName) {
  if (level === 'pre') return `Pre-${stageName}`;
  if (level === 'emerging') return `Emerging ${stageName}`;
  if (level === 'developing') return `Developing ${stageName}`;
  return `Strong ${stageName}`;
}

export function ReadingStageCheck({ ageBracket, onComplete }) {
  const content = AGE_CONTENT[ageBracket] || AGE_CONTENT['6-7'];
  const [checkedIds, setCheckedIds] = useState([]);

  const checkedCount = checkedIds.length;
  const level = computeLevel(checkedCount);
  const label = stageLabel(level, content.stageName);

  const description = useMemo(() => content.descriptions[level], [content.descriptions, level]);

  const toggleMilestone = (id) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '24px 20px' }}>
      <h2 style={{ fontFamily: F, color: NAVY, fontSize: 27, lineHeight: 1.2, marginBottom: 16 }}>{content.heading}</h2>

      <div
        style={{
          background: WARM_WHITE,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 12px rgba(15,43,60,0.08)',
          marginBottom: 18,
        }}
      >
        <div style={{ fontFamily: F, color: NAVY, fontSize: 18, marginBottom: 12 }}>Can your child...</div>

        <div style={{ display: 'grid', gap: 10 }}>
          {content.milestones.map((item) => {
            const selected = checkedIds.includes(item.id);
            return (
              <motion.button
                key={item.id}
                whileTap={{ scale: 0.97 }}
                onClick={() => toggleMilestone(item.id)}
                style={{
                  border: `2px solid ${selected ? TEAL : '#E5EBF0'}`,
                  background: selected ? 'rgba(13,148,136,0.10)' : '#fff',
                  borderRadius: 12,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  transition: 'all 0.15s ease',
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    border: `2px solid ${selected ? TEAL : '#C9D4DD'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    background: selected ? TEAL : 'transparent',
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  {selected ? '✓' : ''}
                </span>
                <span style={{ fontFamily: N, color: NAVY, fontSize: 15, lineHeight: 1.4 }}>{item.label}</span>
              </motion.button>
            );
          })}
        </div>

        <div style={{ height: 1, background: '#E5EBF0', margin: '18px 0 14px' }} />

        <div
          style={{
            display: 'inline-block',
            background: 'rgba(13,148,136,0.12)',
            color: TEAL,
            border: `1px solid ${TEAL}`,
            borderRadius: 999,
            padding: '6px 12px',
            fontFamily: F,
            fontSize: 15,
            marginBottom: 10,
          }}
        >
          {label}
        </div>

        <p style={{ fontFamily: N, color: 'rgba(15,43,60,0.8)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>{description}</p>
      </div>

      <button
        onClick={() => onComplete({ readingChecks: checkedIds, readingStage: label })}
        style={{
          background: TEAL,
          color: '#fff',
          borderRadius: 12,
          padding: '16px 32px',
          fontFamily: F,
          fontSize: 16,
          border: 'none',
          width: '100%',
          cursor: 'pointer',
        }}
      >
        That's My Reader
      </button>
    </div>
  );
}
